import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Alert,
  Card,
  Spin,
  Row,
  message,
  Select,
  Checkbox,
  Modal,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import AuthServices from "../../services/AuthServices";
import "simple-react-validator/dist/locale/es";
import { translate, get_language } from "../../locale";
import UserServices from "../../services/UserServices";
import CustomDatePicker from "../../components/Datepicker/CustomDatePicker";
import moment from "moment";

const terms_url = process.env.REACT_APP_TERMS_OF_USE;
const privacy_url = process.env.REACT_APP_PRIVACY_POLICY;

const Register = () => {
  const { username = "", role, code } = useParams();
  const [values, setValues] = useState({
    username: username,
    password: "",
    confirm: "",
    accept_terms: false,
    enterprise_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [terms, setTerms] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);

  useEffect(() => {
    if (role === "eira-professional") {
      loadCountries();
    }
  }, [role]);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      locale: get_language,
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => (
        <div className="text-red-600 pt-2 text-xs">{message}</div>
      ),
      messages: {
        password:
          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un mínimo de 8 caracteres",
      },
      validators: {
        sameValue: {
          message: translate("Las contraseñas no coinciden"),
          rule: (val, params, validator) => val === params[0],
          required: true,
        },
        date_older_than: {
          message: translate("Debe ser mayor a 18 años"),
          rule: function (val, params) {
            return (
              moment(val).diff(moment().subtract(params[0], "years"), "hours") <
              0
            );
          },
        },
        password: {
          rule: (val, params) => {
            const uppercaseRegExp = /(?=.*?[A-Z])/;
            const lowercaseRegExp = /(?=.*?[a-z])/;
            const digitsRegExp = /(?=.*?[0-9])/;
            const minLengthRegExp = /.{8,}/;

            return (
              val.length >= 8 &&
              uppercaseRegExp.test(val) === true &&
              lowercaseRegExp.test(val) === true &&
              digitsRegExp.test(val) === true &&
              minLengthRegExp.test(val) === true
            );
          },
        },
      },
    })
  );

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await AuthServices.getTerms({
          hash: code,
          email: username,
        });
        if (response.status >= 200 && response.status <= 300) {
          setTerms(response.data.enterprise.terms_conditions);
          if (response.data.enterprise.terms_conditions === "") {
            setValues((v) => ({ ...v, enterprise_terms: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching terms: ", error);
      }
    };

    fetchTerms();
  }, [code, username]);

  const disabled = () => !simpleValidator.current.allValid();

  const loadCountries = async (username) => {
    setLoadingCountries(true);
    setError("");
    try {
      const res = await UserServices.getCountries(username);
      if (res.status >= 200 && res.status < 300) {
        setCountries(
          res.data.countries.map((i) => ({ id: i.code, label: i.name }))
        );
      }
      setLoadingCountries(false);
    } catch (e) {
      setError(e.message);
      setLoadingCountries(false);
    }
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("El usuario y/o contraseña son incorrectos.");
      return;
    }
    try {
      const data = {
        username: values.username,
        password: values.password,
        role: role,
        ...(code ? { hash: code } : null),
        ...(values.first_name ? { first_name: values.first_name } : null),
        ...(values.last_name ? { last_name: values.last_name } : null),
        ...(values.country_code ? { country_code: values.country_code } : null),
        ...(values.birth
          ? { birth: moment(values.birth).format("YYYY-MM-DD") }
          : null),
      };

      const res = await AuthServices.signUp(data);

      if (
        res &&
        res.status !== undefined &&
        res.status >= 200 &&
        res.status < 300 &&
        res.data
      ) {
        setLoading(false);
        setValues({
          username: "",
          password: "",
          confirm: "",
        });
        simpleValidator.current.hideMessages();
        simpleValidator.current.visibleFields = [];
        forceUpdate();

        message.success(translate("Su cuenta ha sido creada exitosamente."));
        navigate("/auth/login");
      } else {
        if (res.data.message === "Invitación expirada") {
          setError(
            "Invitación expirada. Contacta a tu administrador para que te envíe una nueva invitación."
          );
        } else {
          setError("Ocurrio un problema, no pudimos registrar su usuario.");
        }
      }
      setLoading(false);
    } catch (e) {
      console.log(e.message);
      if (e.message !== "") {
        setError(e.message);
      }
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleDatePickerChange = (value) => {
    simpleValidator.current.showMessageFor("birth");
    setValues((v) => ({ ...v, birth: value === null ? "" : value }));
  };

  const handleDatePickerBlur = (value) => {
    simpleValidator.current.showMessageFor("birth");
    setValues((v) => ({ ...v, birth: value === null ? "" : value }));
  };
  const toggleAcceptTerms = () => {
    setValues((v) => ({ ...v, accept_terms: !v.accept_terms }));
  };

  const toggleEnterpriseTerms = () => {
    setValues((v) => ({ ...v, enterprise_terms: !v.enterprise_terms }));
  };
  return (
    <div className="flex flex-row w-screen justify-center items-stretch min-h-full max-w-7xl py-24">
      <div className="w-full md:w-1/2 flex justify-center">
        <Card
          title={translate("Registro")}
          className="w-full lg:w-7/12"
          bordered={false}
          headStyle={{ border: "none" }}
        >
          {error && (
            <Alert
              message={error}
              type="error"
              className="mb-6"
              closable
              onClose={() => setError("")}
            />
          )}

          <Form
            // initialValues={{ username: values.username }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            className="w-full"
          >
            <Form.Item>
              <Input
                placeholder={translate("Nombre de usuario")}
                className="border-custom_primary"
                name="username"
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={username !== undefined}
                value={values.username}
              />
              {simpleValidator.current.message(
                "username",
                values.username,
                "required|email",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Nombre de usuario requerido",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            {role === "eira-professional" && (
              <>
                <Form.Item>
                  <Input
                    placeholder={`${translate("Nombre")}*`}
                    className="border-custom_primary"
                    name="first_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                  />
                  {simpleValidator.current.message(
                    "first_name",
                    values.first_name,
                    "required|min:3",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo Nombre es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Form.Item>
                  <Input
                    placeholder={`${translate("Apellido")}*`}
                    className="border-custom_primary"
                    name="last_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                  />
                  {simpleValidator.current.message(
                    "last_name",
                    values.last_name,
                    "required|min:3",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo Apellido es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Form.Item>
                  <Select
                    name="country_code"
                    style={{
                      border: "red 1px solid",
                      borderRadius: 4,
                    }}
                    placeholder={`${translate("País")}*`}
                    onChange={(v) =>
                      handleChange({
                        target: { name: "country_code", value: v },
                      })
                    }
                    onBlur={(v) => {
                      handleBlur({
                        target: {
                          name: "country_code",
                          value: values.country_code,
                        },
                      });
                    }}
                    value={values.country_code}
                  >
                    {loadingCountries === true && (
                      <Select.Option value="">
                        {translate("Cargando")}...
                      </Select.Option>
                    )}
                    {countries.map((p, k) => (
                      <Select.Option key={k} value={p.id}>
                        {p.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {simpleValidator.current.message(
                    "country_code",
                    values.country_code,
                    "required",
                    get_language === "es"
                      ? {
                          messages: {
                            required: "El campo País es requerido",
                          },
                        }
                      : {}
                  )}
                </Form.Item>
                <Form.Item>
                  {/* <Text>{translate("Fecha de nacimiento")}*</Text> */}
                  <CustomDatePicker
                    style={{
                      border: "red 1px solid",
                      borderRadius: 4,
                    }}
                    onChange={handleDatePickerChange}
                    onClickOutside={handleDatePickerBlur}
                    selected={
                      values.birth ? new Date(moment(values.birth)) : ""
                    }
                    maxDate={new Date(moment().subtract(18, "years"))}
                    locale={get_language}
                    format="DD/MM/YYYY"
                    placeholder={`dfdf*`}
                  />
                  {simpleValidator.current.message(
                    "birth",
                    values.birth,
                    "required|date_older_than:18"
                  )}
                </Form.Item>
              </>
            )}
            <Form.Item>
              <Input.Password
                placeholder={translate("Contraseña")}
                className="border-custom_primary"
                name="password"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {simpleValidator.current.message(
                "password",
                values.password,
                "required|password",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Contraseña es requerido",
                        password:
                          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial (#?!@$%^&*-) y un mínimo de 8 caracteres",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <Input.Password
                placeholder={translate("Confirmar Contraseña")}
                className="border-custom_primary"
                name="confirm"
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {simpleValidator.current.message(
                "confirm",
                values.confirm,
                `required|password|sameValue:${values.password}`,
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Confirmar Contraseña es requerido",
                        password:
                          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial (#?!@$%^&*-) y un mínimo de 8 caracteres",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <Checkbox onChange={toggleAcceptTerms} className="mr-2" />
              Al continuar estoy aceptando los{" "}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={terms_url}
                className="text-custom_primary hover:text-custom_red cursor-pointer"
              >
                términos y condiciones
              </a>
              {" y "}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={privacy_url}
                className="text-custom_primary hover:text-custom_red cursor-pointer"
              >
                política de privacidad
              </a>{" "}
              de Adium.
              {simpleValidator.current.message(
                "accept_terms",
                values.accept_terms,
                "accepted",
                get_language === "es"
                  ? {
                      messages: {
                        accepted:
                          "Debe aceptando los términos y condiciones y política de privacidad para continuar.",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            {terms !== "" && (
              <Form.Item>
                <Checkbox onChange={toggleEnterpriseTerms} className="mr-2" />
                Al continuar estoy aceptando los{" "}
                <a
                  rel="noopener noreferrer"
                  onClick={() => setModalIsOpen(true)}
                  className="text-custom_primary hover:text-custom_red cursor-pointer"
                >
                  términos y condiciones
                </a>{" "}
                de la empresa.
                {simpleValidator.current.message(
                  "enterprise_terms",
                  values.enterprise_terms,
                  "accepted",
                  get_language === "es"
                    ? {
                        messages: {
                          accepted:
                            "Debe aceptar los términos y condiciones para continuar.",
                        },
                      }
                    : {}
                )}
              </Form.Item>
            )}
            <Modal
              width={1100}
              open={modalIsOpen}
              title={translate("Términos y condiciones")}
              onOk={() => setModalIsOpen(false)}
              onCancel={() => setModalIsOpen(false)}
            >
              {terms.split("\n").map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Modal>
            <Row justify="end">
              <Button
                disabled={disabled() || loading}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
              >
                {loading && (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                    className="mr-2"
                  />
                )}
                {translate("Aceptar")}
              </Button>
            </Row>
            <Row className="mt-20">
              <Link to="/auth/login" className="mb-4">
                {translate("Ya tengo cuenta")}
              </Link>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Register;
